import { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { IOption_V2 as IOption } from '.';
import { getLabels } from './utils';
import { IGroup } from '../Options_v2/types';

const EMPTY_SELECTED: IOption[] = [];

const toValueList = (value?: string | number | Array<string | number>): Array<string | number> =>
	Array.isArray(value) ? value : [];

export const useSelect = ({
	value,
	maxSelections,
	onSelect,
	isWithTags,
	options,
	isGroups,
}: {
	value?: string | number | Array<string | number>;
	maxSelections?: number;
	onSelect: (val: string | string[] | number[] | (string | number)[]) => void;
	isWithTags?: boolean;
	options: IOption[] | IGroup[];
	isGroups: boolean;
}) => {
	const [isOpen, setOpen] = useState(false);
	const [visited, setVisited] = useState(false);
	const [toEnter, setToEnter] = useState(false);
	const [selectedValues, setSelectedValues] = useState<IOption[]>(EMPTY_SELECTED);
	const [isMaxError, setIsMaxError] = useState(false);
	const [isOrderBySelection, setIsOrderBySelection] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	const toggleIsOpen = () => setOpen(!isOpen);

	const handleSetOpen = (val: boolean) => setOpen(val);

	const handleRemoveOption = (val: string) => {
		onSelect(toValueList(value).map(String).filter(selected => selected !== String(val)));
		enableSelection();
	};

	const isDisableSelection = maxSelections && toValueList(value).length >= maxSelections;
	const disaleSelection = () => {
		setOpen(false);
		setIsMaxError(true);
	};
	const enableSelection = () => {
		setIsMaxError(false);
	};

	const isValueSelected = (val: string) => {
		return toValueList(value).map(String).includes(val);
	};

	const singleSelectOnChange = (val: string) => {
		onSelect(val);
		setOpen(false);
		if (ref.current) {
			ref.current.focus();
		}
	};

	const multipleSelectOnChange = (val: string) => {
		if (isValueSelected(String(val))) {
			handleRemoveOption(val);
		} else {
			if (isDisableSelection) {
				disaleSelection();
				return;
			} else {
				enableSelection();
			}
			onSelect([...toValueList(value).map(String), val]);
			if (!isOrderBySelection) {
				setIsOrderBySelection(true);
			}
		}
	};

	const handleOnChange = (val: string, isMultiple?: boolean) => {
		if (isMultiple) {
			multipleSelectOnChange(val);
		} else {
			singleSelectOnChange(val);
		}
	};

	useEffect(() => {
		if (toEnter) {
			if (!isOpen) {
				setVisited(true);
			}
		}
		setToEnter(true);
	}, [isOpen]);

	/**
	 * Syncs `selectedValues` with the current `value` / `options` when in tags
	 * mode, so added/removed selections render under the select.
	 *
	 * Bails out via `lodash/isEqual` when the computed selection is structurally
	 * equal to the previous one. Without this guard, unstable `value` / `options`
	 * references from callers would cause a fresh `selectedValues` array on every
	 * render and any consumer that derives state from it would loop.
	 */
	useEffect(() => {
		setSelectedValues(prevSelectedValues => {
			const next: IOption[] =
				isWithTags && Array.isArray(value) && Boolean(options?.length)
					? (getLabels({ prevSelectedValues, options, values: value, isGroups, isOrderBySelection }) as IOption[])
					: EMPTY_SELECTED;

			return isEqual(prevSelectedValues, next) ? prevSelectedValues : next;
		});
	}, [value, isOrderBySelection, options, isWithTags, isGroups]);

	return {
		handleOnChange,
		isOpen,
		visited,
		toEnter,
		selectedValues,
		isMaxError,
		handleSetOpen,
		ref,
		handleRemoveOption,
		isGroups,
		toggleIsOpen,
	};
};
